import React, { useState, useEffect } from "react";
import AuthHelper from "../../helpers/auth-helper/auth-helper";
import FirebaseAuthHelper from "../../helpers/firebase-auth-helper/firebase-auth-helper";
import router from "../../../../shared/top-level-declarations/history";
import { Link } from "react-router-dom";

import "./complete-signup-page.scss";

const CompleteSignupPage = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailInputShown, setEmailInputShown] = useState(false);

  useEffect(() => {
    verifyEmailLink();
  }, []);

  const verifyEmailLink = async () => {
    try {
      // Check if URL is valid signup link
      const url = window.location.href;
      const isValidLink = FirebaseAuthHelper.isSignupLink(url);

      if (!isValidLink) {
        setError("This signup link is invalid or has expired. Please request a new one.");
        setVerifying(false);
        return;
      }

      // Try to get email from localStorage
      let storedEmail = window.localStorage.getItem('emailForSignIn');

      if (!storedEmail) {
        // If no email in storage, show input field for user to enter it
        setEmailInputShown(true);
        setVerifying(false);
        return;
      }

      setEmail(storedEmail);
      setVerifying(false);
    } catch (err) {
      setError("Failed to verify signup link. Please try again.");
      setVerifying(false);
    }
  };

  const handleEmailSubmit = () => {
    if (!email || !validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setEmailInputShown(false);
    setError("");
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleCompleteSignup = async () => {
    setLoading(true);
    setError("");

    // Validation
    if (!name.trim()) {
      setError("Name is required.");
      setLoading(false);
      return;
    }

    if (!organizationName.trim()) {
      setError("Organization name is required.");
      setLoading(false);
      return;
    }

    if (!password) {
      setError("Password is required.");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    try {
      // Complete Firebase sign-in with email link
      const firebaseUser = await AuthHelper.completeSignupWithEmailLink({ email });

      // Update Firebase user password if provided
      if (password) {
        await FirebaseAuthHelper.updatePassword(password);
      }

      // IMPORTANT: Re-authenticate with email/password to establish persistent Firebase session
      // This ensures the session is based on email/password (like login), not the one-time email link
      // Without this, the Firebase session expires on refresh because the email link is no longer valid
      const persistentFirebaseUser = await FirebaseAuthHelper.login({ email, password });

      // Get Firebase ID token from the persistent session
      const firebaseToken = await persistentFirebaseUser.getIdToken(true);

      // Call backend signup endpoint
      await AuthHelper.signup({
        firebaseToken,
        name: name.trim(),
        email,
        password,
        organization: {
          name: organizationName.trim()
        }
      });

      // Navigate to dashboard
      router.navigate("/manage-groups");
    } catch (err) {
      console.error("Signup error:", err);

      // Handle specific error cases
      if (err.message.includes("invalid")) {
        setError("This signup link is invalid or expired. Please request a new one.");
      } else if (err.message.includes("already exists")) {
        setError("An account with this email already exists. Please log in instead.");
      } else if (err.message.includes("token validation error")) {
        setError("Authentication failed. Please try signing up again.");
      } else {
        setError(err.message || "Failed to create account. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="container-fluid complete-signup-bg">
        <div className="row vh-100 d-flex justify-content-center">
          <div className="col-12 align-self-center">
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Verifying your signup link...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (emailInputShown) {
    return (
      <div className="container-fluid complete-signup-bg">
        <div className="row vh-100 d-flex justify-content-center">
          <div className="col-12 align-self-center">
            <div className="card-body">
              <div className="row">
                <div className="col-lg-4 mx-auto">
                  <div className="card">
                    <div className="card-body p-0 auth-header-box">
                      <div className="text-center p-3">
                        <a href="index.html" className="logo logo-admin">
                          <img
                            src="assets/images/logo_new.png"
                            height={50}
                            alt="logo"
                            className="auth-logo"
                          />
                        </a>
                        <h4 className="mt-3 mb-1 fw-semibold font-18">
                          Confirm Your Email
                        </h4>
                      </div>
                    </div>
                    <div className="card-body pt-0">
                      <form className="my-4" onSubmit={(e) => e.preventDefault()}>
                        <p className="text-muted mb-3">
                          Please enter the email address you used to sign up:
                        </p>
                        <div className="form-group mb-3">
                          <label className="form-label" htmlFor="email">
                            Email Address
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            id="email"
                            name="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                        </div>

                        <div className="form-group mb-0">
                          <div className="d-grid">
                            <button
                              className="bouncy-button small"
                              type="button"
                              onClick={handleEmailSubmit}
                            >
                              Continue <i className="fas fa-arrow-right ms-1" />
                            </button>
                          </div>
                        </div>

                        {error && (
                          <div className="errorMessage mt-3">
                            <p className="text-danger mb-0">{error}</p>
                          </div>
                        )}
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid complete-signup-bg">
      <div className="row vh-100 d-flex justify-content-center">
        <div className="col-12 align-self-center">
          <div className="card-body">
            <div className="row">
              <div className="col-lg-5 mx-auto">
                <div className="card">
                  <div className="card-body p-0 auth-header-box">
                    <div className="text-center p-3">
                      <a href="index.html" className="logo logo-admin">
                        <img
                          src="assets/images/logo_new.png"
                          height={50}
                          alt="logo"
                          className="auth-logo"
                        />
                      </a>
                      <h4 className="mt-3 mb-1 fw-semibold font-18">
                        Complete Your Signup
                      </h4>
                      <p className="text-muted mb-0">
                        Just a few more details to get started
                      </p>
                    </div>
                  </div>
                  <div className="card-body pt-0">
                    <form
                      className="my-4"
                      onSubmit={(e) => e.preventDefault()}
                    >
                      <div className="mb-3">
                        <h6 className="text-muted mb-3">Your Details</h6>

                        <div className="form-group mb-3">
                          <label className="form-label" htmlFor="name">
                            Full Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="name"
                            name="name"
                            placeholder="Enter your full name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={loading}
                          />
                        </div>

                        <div className="form-group mb-3">
                          <label className="form-label" htmlFor="email-display">
                            Email Address
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            id="email-display"
                            value={email}
                            disabled
                          />
                        </div>

                        <div className="form-group mb-2">
                          <label className="form-label" htmlFor="password">
                            Password <span className="text-danger">*</span>
                          </label>
                          <input
                            type={showPassword ? "text" : "password"}
                            className="form-control"
                            id="password"
                            name="password"
                            placeholder="Create a password (min 6 characters)"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                            autoComplete="new-password"
                          />
                        </div>

                        <div className="password-toggle mb-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="showPasswordToggle"
                            checked={showPassword}
                            onChange={() => setShowPassword(!showPassword)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="showPasswordToggle"
                          >
                            Show Password
                          </label>
                        </div>

                        <div className="form-group mb-2">
                          <label className="form-label" htmlFor="confirmPassword">
                            Confirm Password <span className="text-danger">*</span>
                          </label>
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            className="form-control"
                            id="confirmPassword"
                            name="confirmPassword"
                            placeholder="Confirm your password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            disabled={loading}
                            autoComplete="new-password"
                          />
                        </div>

                        <div className="password-toggle mb-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="showConfirmPasswordToggle"
                            checked={showConfirmPassword}
                            onChange={() => setShowConfirmPassword(!showConfirmPassword)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="showConfirmPasswordToggle"
                          >
                            Show Password
                          </label>
                        </div>
                      </div>

                      <div className="mb-3">
                        <h6 className="text-muted mb-3">Organization Details</h6>

                        <div className="form-group mb-3">
                          <label className="form-label" htmlFor="organizationName">
                            Organization Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="organizationName"
                            name="organizationName"
                            placeholder="Enter your organization name"
                            value={organizationName}
                            onChange={(e) => setOrganizationName(e.target.value)}
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="form-group mb-0">
                        <div className="d-grid">
                          <button
                            className="bouncy-button small"
                            type="button"
                            onClick={handleCompleteSignup}
                            disabled={loading}
                          >
                            {loading ? "Creating Account..." : "Create Account"}{" "}
                            <i className="fas fa-check ms-1" />
                          </button>
                        </div>
                      </div>

                      {error && (
                        <div className="errorMessage mt-3">
                          <p className="text-danger mb-0">{error}</p>
                        </div>
                      )}

                      <div className="text-center mt-4">
                        <small>
                          Already have an account? <Link to="/login">Log in</Link>
                        </small>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteSignupPage;
